<!--
  A tabela da gestão de preço de adicional POR FRANQUIA — a irmã da
  SOServicePrioritiesTable, mais enxuta: sem clone e sem edição inline (o
  modal/form é a superfície de edição; a linha tem os botões Editar e
  Desligar/Religar).

  A coluna "Default global" fica AO LADO do preço da franquia de propósito:
  é a referência contra a qual o local diverge — a pergunta do operador é
  "quanto esta franquia foge da rede?".
-->
<template>
  <data-table
    ref="datatableRef"
    :config="datatableConfig"
    :endpoint="endpoint"
    :autoLoad="true"
    :refreshButton="{ show: true, text: t('Atualizar') }"
    :csvButton="{ show: true, text: t('CSV') }"
    @data-error="handleDataError"
    @button-click="handleButtonClick"
  />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';
import { useSOAdditionalServicePrices } from '../../composables/useSOAdditionalServicePrices';
import DataTable from '../../grid/DataTable.vue';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface PriceRow {
  so_additional_service_price_id?: Id;
  so_additional_service_price_additional_id?: Id;
  so_additional_service_price_franchise_id?: Id;
  so_additional_service_price_price?: number;
  so_additional_service_price_deadline_days?: number;
  so_additional_service_price_active?: number;
  so_additional_service_name?: string;
  so_additional_service_default_price?: number;
  so_service_category_name?: string;
  franquia?: string;
  [key: string]: unknown;
}

const { t } = useI18n();
const { save } = useSOAdditionalServicePrices();

const emit = defineEmits<{
  edit: [id: Id];
  delete: [id: Id, data: PriceRow | null];
}>();

const datatableRef = ref<DataTableInstance | null>(null);

const endpoint = computed<string>(() => '/index.php/so/additional_service_prices/lista');

const comoLinha = (row: Record<string, unknown>): PriceRow => row as unknown as PriceRow;

const tableColumns = computed(() => [
  {
    data: 'so_additional_service_price_id',
    title: t('ID'),
    width: 60,
    sortable: true
  },
  {
    data: 'franquia',
    title: t('Franquia'),
    width: 180,
    sortable: true,
    filterType: 'text'
  },
  {
    data: 'so_additional_service_name',
    title: t('Serviço adicional'),
    sortable: true,
    filterType: 'text'
  },
  {
    data: 'so_service_category_name',
    title: t('Categoria'),
    width: 130,
    sortable: true,
    filterType: 'text'
  },
  {
    data: 'so_additional_service_price_price',
    title: t('Preço da franquia'),
    width: 140,
    sortable: true,
    renderType: 'currency'
  },
  {
    data: 'so_additional_service_default_price',
    title: t('Default global'),
    width: 130,
    sortable: true,
    renderType: 'currency'
  },
  {
    data: 'so_additional_service_price_deadline_days',
    title: t('Prazo adicional (dias)'),
    width: 130,
    sortable: true,
    renderType: 'integer'
  },
  {
    data: 'so_additional_service_price_active',
    title: t('Situação'),
    width: 120,
    sortable: true,
    renderType: 'badge',
    badgeConfig: (row: Record<string, unknown>) => {
      const ativo = Number(comoLinha(row).so_additional_service_price_active) === 1;
      return {
        label: ativo ? t('Ativo') : t('Desligado'),
        severity: ativo ? 'success' : 'secondary',
        tooltip: ativo
          ? t('Oferecido no PDV desta franquia, com este preço')
          : t('A franquia desligou este adicional — ele não aparece no PDV dela')
      };
    }
  },
  {
    data: null,
    title: t('Editar'),
    width: 110,
    sortable: false,
    renderType: 'clickable_button',
    buttonText: t('Editar'),
    buttonIcon: 'pi pi-pencil',
    buttonAction: 'editar',
    buttonTestId: (row: Record<string, unknown>) =>
      `so-additional-service-prices-edit-${comoLinha(row).so_additional_service_price_id}`
  },
  {
    data: null,
    title: t('Ação'),
    width: 130,
    sortable: false,
    renderType: 'clickable_button',
    // buttonText e string no tipo do DataTable — o LADO do toggle vai no
    // tooltip por linha, e o handler decide pelo estado real.
    buttonText: t('Ligar/Desligar'),
    buttonIcon: 'pi pi-power-off',
    buttonAction: 'alternar',
    buttonTooltip: (row: Record<string, unknown>) =>
      Number(comoLinha(row).so_additional_service_price_active) === 1
        ? t('Desligar este adicional para a franquia')
        : t('Religar este adicional para a franquia'),
    buttonTestId: (row: Record<string, unknown>) =>
      `so-additional-service-prices-toggle-${comoLinha(row).so_additional_service_price_id}`
  }
]);

const datatableConfig = computed(() => ({
  tableId: 'so-additional-service-prices',
  rowTestId: (data: Record<string, unknown>) => {
    const id = comoLinha(data).so_additional_service_price_id;
    return id == null ? '' : `so-additional-service-prices-row-${id}`;
  },
  filename: `so_additional_service_prices_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  pageLength: 25,
  pagination: true,
  sortable: true,
  showFilters: true,
  filterDisplay: 'row',
  stripedRows: true,
  showGridlines: true,
  size: 'small'
}));

const loadData = (): void => {
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataError = (error: { message?: string } | Error): void => {
  const message = error instanceof Error ? error.message : (error.message || '');
  eventBus.emit('globalMessage.error', t('Erro ao carregar preços de adicionais') + ': ' + message);
};

/**
 * Religar é `salvar` com active=1 sobre a linha existente; Desligar abre a
 * confirmação (modal) porque tira o adicional do PDV da franquia na hora.
 */
const handleButtonClick = async (evento: { data: Record<string, unknown> | null; action?: unknown }): Promise<void> => {
  if (!evento.data) return;
  const linha = comoLinha(evento.data);
  const id = linha.so_additional_service_price_id;
  if (id == null) return;

  if (evento.action === 'editar') {
    emit('edit', id);
    return;
  }

  if (evento.action !== 'alternar') return;

  if (Number(linha.so_additional_service_price_active) === 1) {
    emit('delete', id, linha);
    return;
  }

  try {
    await save({
      so_additional_service_price_id: Number(id),
      so_additional_service_price_additional_id: Number(linha.so_additional_service_price_additional_id),
      // O wire da listagem fala REAIS; salvar fala CENTAVOS.
      so_additional_service_price_price: Math.round((linha.so_additional_service_price_price || 0) * 100),
      ...(linha.so_additional_service_price_deadline_days != null
        ? { so_additional_service_price_deadline_days: Number(linha.so_additional_service_price_deadline_days) }
        : {}),
      so_additional_service_price_active: 1
    });
    eventBus.emit('globalMessage.success', t('Adicional religado para a franquia'));
    loadData();
  } catch (error: unknown) {
    logError('SOAdditionalServicePricesTable', 'handleButtonClick', 'Erro ao religar', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao religar o adicional') + ': ' + message);
  }
};

defineExpose({
  loadData
});
</script>
